import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '../contexts/Query';
import { useBalloonEventsMetadata } from '../contexts/Metadata';
import { useApi, useApiFetch } from '../contexts/Api';
import { pendingAutoFit } from 'ts-core';
import dayjs from 'dayjs';

export type FilterType = 'tags' | 'categories' | 'places' | 'sources' | 'dates' | 'search' | 'user';

interface FilterRouteHandlerProps {
    type: FilterType;
    onFilterApplied?: (type: FilterType, value: string) => void;
    onFilterError?: (type: FilterType, value: string, error: string) => void;
}

interface DatePreset {
    from: Date;
    to: Date;
}

function getDatePreset(preset: string): DatePreset | null {
    const now = dayjs();
    const presetLower = preset.toLowerCase();
    
    switch (presetLower) {
        case 'today':
            return {
                from: now.startOf('day').toDate(),
                to: now.endOf('day').toDate()
            };
        case 'tomorrow':
            return {
                from: now.add(1, 'day').startOf('day').toDate(),
                to: now.add(1, 'day').endOf('day').toDate()
            };
        case 'this-week':
            return {
                from: now.startOf('week').toDate(),
                to: now.endOf('week').toDate()
            };
        case 'this-weekend': {
            const saturday = now.day(6).startOf('day');
            const sunday = now.day(0).add(1, 'week').endOf('day');
            return {
                from: saturday.toDate(),
                to: sunday.toDate()
            };
        }
        case 'this-month':
            return {
                from: now.startOf('month').toDate(),
                to: now.endOf('month').toDate()
            };
        case 'next-week':
            return {
                from: now.add(1, 'week').startOf('week').toDate(),
                to: now.add(1, 'week').endOf('week').toDate()
            };
        case 'next-month':
            return {
                from: now.add(1, 'month').startOf('month').toDate(),
                to: now.add(1, 'month').endOf('month').toDate()
            };
        default:
            return null;
    }
}

export function FilterRouteHandler({ 
    type, 
    onFilterApplied,
    onFilterError 
}: FilterRouteHandlerProps) {
    const { slug, preset } = useParams<{ slug?: string; preset?: string }>();
    const { addQuery, has } = useQuery();
    const { metadata } = useBalloonEventsMetadata();
    const { apiEndpoints, apiKey } = useApi();
    const apiFetch = useApiFetch();
    const appliedRef = useRef<string | null>(null);
    
    const value = slug || preset || '';
    const valueLower = value.toLowerCase();

    useEffect(() => {
        const filterKey = `${type}:${value}`;
        if (appliedRef.current === filterKey || !value) return;
        
        const applyFilter = async () => {
            switch (type) {
                case 'tags': {
                    const queryId = `tag:${valueLower}`;
                    if (!has(queryId)) {
                        // Arm one-shot map auto-fit: the next search will request
                        // the global bbox and MapAutoFitHandler fits to it once.
                        pendingAutoFit.arm();
                        addQuery({ id: queryId, tags: [valueLower] }, false);
                    }
                    appliedRef.current = filterKey;
                    onFilterApplied?.(type, valueLower);
                    break;
                }
                
                case 'categories': {
                    if (!metadata) return;
                    const category = metadata.getCategoryBySlug(valueLower);
                    if (category) {
                        const query = { id: category.id, categories: [category.id] };
                        if (!has(category.id)) {
                            // Arm one-shot map auto-fit (see tags case above).
                            pendingAutoFit.arm();
                            addQuery(query, false);
                        }
                        appliedRef.current = filterKey;
                        onFilterApplied?.(type, category.slug);
                    } else {
                        console.warn('[FilterRouteHandler] Category not found:', value);
                        onFilterError?.(type, value, 'Category not found');
                    }
                    break;
                }
                
                case 'places': {
                    if (!apiEndpoints?.regions || !apiKey) return;
                    try {
                        const response = await apiFetch(
                            `${apiEndpoints.regions}?name=${encodeURIComponent(value)}`,
                            { method: 'GET' }
                        );
                        const data = await response.json();
                        const regions = data.regions || [];
                        const region = regions.find(
                            (r: { name: string; slug: string }) => 
                                r.name.toLowerCase() === valueLower || 
                                r.slug.toLowerCase() === valueLower
                        );
                        
                        if (region) {
                            const queryId = `region:${region.slug}`;
                            if (!has(queryId)) {
                                addQuery({ id: queryId, region: [region.slug] }, false);
                            }
                            appliedRef.current = filterKey;
                            onFilterApplied?.(type, region.name);
                        } else {
                            console.warn('[FilterRouteHandler] Place not found:', value);
                            onFilterError?.(type, value, 'Place not found');
                        }
                    } catch (error) {
                        console.error('[FilterRouteHandler] Failed to fetch place:', error);
                        onFilterError?.(type, value, 'Failed to fetch place');
                    }
                    break;
                }
                
                case 'sources': {
                    const queryId = `source:${valueLower}`;
                    if (!has(queryId)) {
                        addQuery({ id: queryId, sources: [valueLower] }, false);
                    }
                    appliedRef.current = filterKey;
                    onFilterApplied?.(type, valueLower);
                    break;
                }

                case 'user': {
                    const queryId = `user:${valueLower}`;
                    if (!has(queryId)) {
                        addQuery({ id: queryId, user: [valueLower] }, false);
                    }
                    appliedRef.current = filterKey;
                    onFilterApplied?.(type, valueLower);
                    break;
                }
                
                case 'dates': {
                    const datePreset = getDatePreset(value);
                    if (datePreset) {
                        const queryId = `dates:${valueLower}`;
                        if (!has(queryId)) {
                            addQuery({ id: queryId, dates: [datePreset.from, datePreset.to] }, false);
                        }
                        appliedRef.current = filterKey;
                        onFilterApplied?.(type, valueLower);
                    } else {
                        console.warn('[FilterRouteHandler] Invalid date preset:', value);
                        onFilterError?.(type, value, 'Invalid date preset');
                    }
                    break;
                }
            }
        };
        
        applyFilter();
    }, [type, value, valueLower, metadata, apiEndpoints, apiKey, apiFetch, addQuery, has, onFilterApplied, onFilterError]);

    return null;
}

export default FilterRouteHandler;
